import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '@/components/common/PageHeader';
import { Spinner } from '@/components/common/Spinner';
import { faqHostApi } from '@/api/services/faqHost';
import type { FaqCategoryViewModel, FaqDto } from '@/api/types/faqHost';

// User FAQ viewer: per-category card, a 2-column accordion (even-index FAQs left,
// odd-index right), HTML answers, and #c{id} hash deep-linking (opens + scrolls).
export function FaqPage() {
  const query = useQuery({ queryKey: ['faq', 'list'], queryFn: () => faqHostApi.getListWithFaqs() });
  const [open, setOpen] = useState<Set<string>>(new Set());

  const toggle = (id: string) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });

  // Hash deep-link: #c{faqId} opens that panel and scrolls to it.
  useEffect(() => {
    const openByHash = () => {
      const hash = window.location.hash;
      if (!hash.startsWith('#c')) return;
      const id = hash.slice(2);
      setOpen(new Set([id]));
      const el = document.getElementById(`c${id}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    openByHash();
    window.addEventListener('hashchange', openByHash);
    return () => window.removeEventListener('hashchange', openByHash);
  }, [query.data]);

  if (query.isLoading) return <Spinner />;
  const categories = query.data ?? [];

  const item = (faq: FaqDto) => {
    const isOpen = open.has(faq.id);
    return (
      <div className="accordion-item" key={faq.id}>
        <h2 className="accordion-header" id={`h${faq.id}`}>
          <button type="button" className={`accordion-button${isOpen ? '' : ' collapsed'}`} aria-expanded={isOpen} onClick={() => toggle(faq.id)}>
            <span className="flex-grow-1 text-truncate" dangerouslySetInnerHTML={{ __html: faq.question }} />
          </button>
        </h2>
        <div id={`c${faq.id}`} className={`accordion-collapse collapse${isOpen ? ' show' : ''}`}>
          <div className="accordion-body text-muted" dangerouslySetInnerHTML={{ __html: faq.answer }} />
        </div>
      </div>
    );
  };

  return (
    <div className="faq-page">
      <PageHeader title="FAQ" breadcrumb={['Home', 'FAQ']} />
      <h2 className="text-center mb-4">Frequently Asked Questions</h2>

      {categories.length === 0 && <p className="text-center text-muted">No FAQs available.</p>}

      {categories.map((cat: FaqCategoryViewModel) => {
        const left = cat.faqs.filter((_, i) => i % 2 === 0);
        const right = cat.faqs.filter((_, i) => i % 2 === 1);
        return (
          <div className="card custom-card mb-4" key={cat.id}>
            <div className="card-header">
              <span className="h4 m-0" id={`sec-${cat.id}`}>{cat.title}</span>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-lg-6"><div className="accordion faq-accordion">{left.map(item)}</div></div>
                <div className="col-lg-6"><div className="accordion faq-accordion">{right.map(item)}</div></div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
